import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于桃子影院',
              description: '桃子影院是领先的免费在线影视平台，提供国产剧、韩剧、美剧、日剧、动漫、综艺等电视剧全集在线观看服务。',
              url: 'https://www.taozitv.com/about',
              isPartOf: { '@type': 'WebSite', name: '桃子影院', url: 'https://www.taozitv.com/' },
            }),
          }}
        />

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 lg:px-8 py-4 bg-background-100/50 border-b border-background-200/30">
          <div className="max-w-[1200px] mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-400 transition-colors whitespace-nowrap">首页</Link>
              <span className="text-foreground-700">/</span>
              <span className="text-foreground-300">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 text-center">
          <div className="max-w-[800px] mx-auto">
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-400">
              <i className="ri-movie-2-fill text-3xl md:text-4xl"></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-100 mb-4">
              关于桃子影院
            </h1>
            <p className="text-foreground-500 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              桃子影院成立于2020年，是领先的免费在线影视平台。我们致力于为全球华语用户提供最优质的在线观影体验，
              汇聚海量电视剧资源，涵盖国产剧、韩剧、美剧、日剧、动漫、综艺等多元内容。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-100/50">
          <div className="max-w-[1000px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-100 mb-5">
                  我们的故事
                </h2>
                <div className="space-y-4 text-foreground-500 text-sm leading-relaxed">
                  <p>
                    桃子影院的诞生源于一个简单的愿望——让每个人都能轻松看到自己喜欢的电视剧。
                    我们的创始团队发现，市面上很多影视平台要么收费昂贵，要么广告泛滥。于是我们决定打造一个真正以用户为中心的免费观影平台。
                  </p>
                  <p>
                    经过数年的不懈努力，桃子影院已经成长为拥有<strong className="text-primary-400">10万+</strong>影视资源、
                    <strong className="text-primary-400">5000万+</strong>活跃用户的头部平台。
                    我们持续投入技术研发，确保每一位用户都能享受到流畅、高清的观影体验。
                  </p>
                  <p>
                    我们的愿景是成为全球最受欢迎的华语影视平台，让优质内容无界限地触达每一位观众。
                  </p>
                </div>
              </div>
              <div className="aspect-[16/10] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20large%20screens%20displaying%20film%20content%2C%20warm%20ambient%20lighting%2C%20collaborative%20workspace%2C%20tech%20company%20culture%2C%20clean%20modern%20design%2C%20cinematic%20color%20grading&width=800&height=500&seq=about-brand-story&orientation=landscape"
                  alt="桃子影院的办公环境"
                  title="桃子影院 - 办公环境"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20">
          <div className="max-w-[1000px] mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-100 mb-3">
                我们的核心价值观
              </h2>
              <p className="text-foreground-600 text-sm">
                驱动桃子影院不断前行的信念
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  icon: 'ri-heart-line',
                  title: '用户至上',
                  desc: '一切以用户体验为核心，倾听每一位用户的反馈，持续优化产品功能与内容推荐。',
                },
                {
                  icon: 'ri-sparkling-line',
                  title: '技术创新',
                  desc: '不断探索前沿技术，自研视频编解码与CDN加速方案，确保高清画质与流畅播放。',
                },
                {
                  icon: 'ri-scales-line',
                  title: '内容为本',
                  desc: '尊重版权，倡导正版。与优质内容方合作，为用户提供丰富多元的影视作品。',
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/40 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-400">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-foreground-200 font-heading text-base mb-3">{item.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Awards */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-100/50">
          <div className="max-w-[1000px] mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-100 mb-3">
                业界口碑
              </h2>
              <p className="text-foreground-600 text-sm">
                来自用户与行业的认可
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  quote: '桃子影院是我用过的最好的免费追剧平台！画质清晰，更新速度快，完全没有广告干扰。强烈推荐！',
                  author: '小明',
                  role: '资深追剧用户',
                },
                {
                  quote: '作为韩剧迷，找资源一直很头疼。自从发现了桃子影院，从此追剧无忧。感谢平台提供这么好的服务。',
                  author: '追剧小达人',
                  role: '韩剧爱好者',
                },
                {
                  quote: '不仅是内容丰富，更重要的是播放体验太好了。4K画质真的惊艳，而且秒开不卡顿。',
                  author: '影视鉴赏家',
                  role: '影视爱好者',
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/40"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className="w-4 h-4 flex items-center justify-center text-accent-500">
                        <i className="ri-star-fill text-sm"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-4">
                    "{item.quote}"
                  </p>
                  <div>
                    <p className="text-foreground-300 text-sm font-medium">{item.author}</p>
                    <p className="text-foreground-600 text-xs">{item.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20">
          <div className="max-w-[800px] mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-100 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-600 text-sm">
                有任何问题或建议，欢迎随时联系我们
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  icon: 'ri-mail-line',
                  title: '电子邮件',
                  content: 'contact@taozitv.com',
                  desc: '商务合作与媒体咨询',
                },
                {
                  icon: 'ri-customer-service-line',
                  title: '在线客服',
                  content: '7×24小时在线',
                  desc: '技术问题与使用帮助',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '内容举报',
                  content: 'report@taozitv.com',
                  desc: '侵权投诉与内容反馈',
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/40 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-400">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-foreground-200 font-heading text-base mb-2">{item.title}</h3>
                  <p className="text-primary-400 text-sm mb-1">{item.content}</p>
                  <p className="text-foreground-600 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-100/50">
          <div className="max-w-[800px] mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-100 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-600 text-sm">
                关于桃子影院的常见疑问解答
              </p>
            </div>
            <div className="space-y-3">
              {[
                {
                  q: '桃子影院真的完全免费吗？',
                  a: '是的！桃子影院承诺永久免费，所有电视剧资源均可免费观看，无需充值会员，无需付费。我们通过广告和品牌合作维持运营，确保用户可以零成本享受高品质观影体验。',
                },
                {
                  q: '支持哪些画质？最高可以看什么清晰度？',
                  a: '桃子影院支持标清、高清、超清、蓝光、4K等多种画质选择，用户可以根据自己的网络环境和设备性能自由切换。推荐在WiFi环境下选择4K画质获得最佳观影体验。',
                },
                {
                  q: '电视剧更新速度如何？能第一时间看到最新剧集吗？',
                  a: '我们的内容团队全天候跟进各大平台的最新更新，热门剧集通常在播出后24小时内即可在桃子影院观看。对于热门连载剧集，我们会设置更新提醒，确保您不会错过任何一集。',
                },
                {
                  q: '可以在手机和电视上观看吗？',
                  a: '当然可以！桃子影院支持手机、平板、电脑和智能电视等多种设备访问。无论您使用iOS、Android、Windows还是Mac，都可以通过浏览器直接访问，无需下载任何App。',
                },
                {
                  q: '遇到播放问题怎么办？',
                  a: '如果您在使用过程中遇到任何播放问题，可以尝试以下方法：1) 切换至较低画质；2) 刷新页面；3) 清除浏览器缓存；4) 联系我们的在线客服获取实时帮助。我们的技术团队会持续优化播放体验。',
                },
              ].map((item, idx) => (
                <details key={idx} className="group rounded-lg bg-background-50 border border-background-200/40 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                    <span className="text-foreground-300 text-sm font-medium pr-4">{item.q}</span>
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-500 group-open:rotate-180 transition-transform shrink-0">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-foreground-500 text-sm leading-relaxed">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}